import { Fragment, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import intl from 'react-intl-universal';
import { useHistory } from 'react-router-dom';
import get from 'lodash/get';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  getForms,
  selectFormsForTable,
  setPagination,
  setSearchParam,
  setSorting,
  duplicateForm,
  selectFormStatus,
  deleteForm,
  deleteJsonSchemaForm,
} from 'state/Forms/formSlice';
import { selectOrganizationId, selectUserId } from 'state/User/userSlice';
import {
  displayNotification,
  setNotificationText,
  setNotificationTimeout,
  setNotificationVariant,
} from 'state/InlineNotification/inlineNotificationSlice';
import { showNotification } from 'state/SnackbarNotification/SnackbarNotificationSlice';
import { SortingOrderType } from 'utils/customTypes';
import { PATHS, V2_PATHS, SLICE_STATUS } from 'utils/constants';
import Pagination from 'Organisms/Pagination/ClientPagination';
import FormsTable from './FormsTable';
import {
  getAllUsers,
  getLDUsers,
} from 'state/UsersManagement/usersManagementSlice';

const FormsPage = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const organizationId = useSelector(selectOrganizationId);
  const formStatus = useSelector(selectFormStatus);
  const userId = useSelector(selectUserId);
  const forms = useSelector(selectFormsForTable);
  const { advancedIntake = false } = useFlags();

  useEffect(() => {
    if (organizationId) {
      dispatch(getLDUsers());
      // selectFormsForTable resolves each form's creator from `allUsers`, which
      // only getAllUsers populates; without this the creator never resolves on a
      // fresh load and every seeded form falls through to the System label even
      // when it has a live creator (CGP-212).
      dispatch(getAllUsers());
      dispatch(getForms({ organizationId }));
      dispatch(setSearchParam(''));
    }
  }, [organizationId, dispatch]);

  useEffect(() => {
    const formDeleted = sessionStorage.getItem('formDeletedSuccess');
    if (formDeleted) {
      sessionStorage.removeItem('formDeletedSuccess');
      dispatch(
        showNotification({
          notificationVariant: 'success',
          notificationTitle: intl.get('SETTINGS_PAGE.FORMS.DELETE.SUCCESS'),
        })
      );
    }
  }, [dispatch]);

  const updatePagination = useCallback(
    (params) => {
      dispatch(setPagination(params));
    },
    [dispatch]
  );

  const onSearch = useCallback(
    (title: string) => {
      dispatch(setSearchParam(title));
    },
    [dispatch]
  );

  const onSort = useCallback(
    (sorting: SortingOrderType) => {
      dispatch(setSorting(sorting));
    },
    [dispatch]
  );

  const onDuplicate = useCallback(
    async (
      title: string,
      formId: string,
      redirect: boolean,
      isJsonSchema?: boolean
    ) => {
      if (userId) {
        const response = await dispatch(
          duplicateForm({
            formId,
            title,
            creatorId: userId,
            isJsonSchema,
            advancedIntake,
          })
        );

        if (formStatus === SLICE_STATUS.IDLE) {
          dispatch(
            setNotificationText(
              intl.get('SETTINGS_PAGE.FORMS.FORM_CREATED_SUCCESS')
            )
          );
          dispatch(setNotificationVariant('success'));
          dispatch(setNotificationTimeout(4000));
          dispatch(displayNotification());
          if (redirect) {
            const newFormId = get(response, 'payload.id');
            if (advancedIntake && isJsonSchema) {
              window.location.href = `${V2_PATHS.FORM_BUILDER}/${newFormId}?accountId=${organizationId}`;
            } else {
              history.push(`${PATHS.SETTINGS}${PATHS.FORM_PAGE}/${newFormId}`);
            }
          }
        }
      }
    },
    [advancedIntake, dispatch, formStatus, history, organizationId, userId]
  );

  const onDelete = useCallback(
    async (formId: string, isJsonSchema?: boolean) => {
      if (formId) {
        if (advancedIntake && isJsonSchema) {
          await dispatch(deleteJsonSchemaForm(formId));
        } else {
          await dispatch(deleteForm(formId));
        }

        if (formStatus === SLICE_STATUS.IDLE) {
          dispatch(
            setNotificationText(intl.get('SETTINGS_PAGE.FORMS.DELETE.SUCCESS'))
          );
          dispatch(setNotificationVariant('success'));
          dispatch(setNotificationTimeout(4000));
          dispatch(displayNotification());
        }
      }
    },
    [advancedIntake, dispatch, formStatus]
  );

  return (
    <Fragment>
      <FormsTable
        forms={forms.data}
        onSearch={onSearch}
        onSort={onSort}
        onDuplicate={onDuplicate}
        onDelete={onDelete}
      />
      <Pagination total={forms.total} onChange={updatePagination} />
    </Fragment>
  );
};

export default FormsPage;
